import {
	Activity,
	AlertTriangle,
	Ban,
	Building2,
	ChevronDown,
	CircleArrowUp,
	Clock,
	Copy,
	Cpu,
	Database,
	FileText,
	FlaskConical,
	FolderSync,
	HardDrive,
	KeyRound,
	Laptop,
	Loader2,
	type LucideIcon,
	MemoryStick,
	Monitor,
	MonitorX,
	Pencil,
	Printer,
	RotateCcw,
	Send,
	Server,
	ShieldAlert,
	ShieldCheck,
	StickyNote,
	Store,
	Trash2,
	Wrench,
} from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
	ConfirmDialog,
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/features/auth/use-auth";
import { DeviceSyncSection } from "@/features/synchronizacja-apteki/device-sync-section";
import { versionWithBuild } from "@/features/synchronizacja-apteki/format";
import { ApiError } from "@/lib/auth-api";
import { badgeBase, type StatusVariant, statusClasses } from "@/lib/colors";
import { pickLazyComponent } from "@/lib/dynamic-import";
import { cn } from "@/lib/utils";
import { ConnectButton } from "./connect-button";
import { DeleteDeviceDialog } from "./delete-device-dialog";
import { DeviceOperatorNote } from "./device-operator-note";
import type {
	ConnectionPolicy,
	DeviceAgent,
	DeviceOperation,
	DeviceRole,
	DeviceStatus,
	MetricsTriple,
	OptinKind,
} from "./devices-api";
import {
	connectionPolicyLabel,
	deviceLabel,
	effectiveDeviceStatus,
	emojifyEmoticons,
	formatBootTypeHint,
	formatCacheAge,
	formatDiskAlert,
	formatMetricPct,
	formatPrinterQueueCount,
	formatRustdeskServiceState,
	formatUptime,
	hasAnyMetric,
	isAgentVersionOlder,
	isDeviceReachable,
	metricsLevel,
} from "./devices-helpers";
import { useDotnetInstallMutation, useDotnetRuntimeQuery } from "./dotnet-runtime-api";
import { installBlockReason, notifyInstallResult } from "./dotnet-runtime-sheet";
import { EditDeviceDialog } from "./edit-device-dialog";
import { StationNumber } from "./identify-stations";
import { ShareHealthSection } from "./share-health-section";
import {
	downloadLogBundle,
	useDbOnlineMutation,
	useDeviceDiagnosticsQuery,
	useDeviceMetricsSummaryQuery,
	useDeviceNotesQuery,
	useDeviceOperationsQuery,
	useDeviceShareHealthQuery,
	useGroupExclusions,
	useInstallerMetadataQuery,
	useKillOfficeMutation,
	useKsShareFixMutation,
	useLeaveNoteMutation,
	useLogBundleStatusQuery,
	useNotificationOptin,
	usePurgeRustdeskMutation,
	useRequestAgentUpdateMutation,
	useRequestLogBundleMutation,
	useRestartSpoolerMutation,
	useRevealConnectionSecret,
	useRotatePasswordMutation,
	useRunDeviceOperationMutation,
	useSetGroupExclusion,
	useSetNotificationOptin,
	useUnlockServiceAccountMutation,
	useUpdateAsystentEnabled,
	useUpdateConnectionPolicy,
	useUpdateDeviceCanary,
} from "./use-devices-query";
import { useDeviceEventIncidentsQuery } from "./use-event-rules";

// Wykresy jakości internetu leniwie: recharts ma własny chunk (vite.config.ts)
// i nie może wchodzić do każdej wizyty na liście urządzeń. `pickLazyComponent`
// zamiast inline `m.X` — przeżywa stary chunk po deployu (FARMASTER-FRONTEND-2H).
const NetworkQualitySection = lazy(() =>
	import("./network-quality-section").then((m) =>
		pickLazyComponent<typeof m.NetworkQualitySection>(m, "NetworkQualitySection"),
	),
);

/** Sentinel select value standing in for the `null` (auto) override. */
const POLICY_AUTO = "auto";

/**
 * Aktywne zdarzenia regułowe urządzenia (uptime/dysk) — widoczne dopiero w
 * rozwiniętym kaflu. Na zwiniętej belce jest tylko sam wykrzyknik.
 */
function ActiveEventsSection({ uuid }: { uuid: string }) {
	const { data, isLoading } = useDeviceEventIncidentsQuery(uuid, true);
	if (isLoading || !data || data.length === 0) return null;
	return (
		<div className="space-y-1.5 rounded-md border border-warning/40 bg-warning/10 p-2">
			<div className="flex items-center gap-1.5 text-xs font-medium text-warning-foreground">
				<AlertTriangle className="h-3.5 w-3.5" aria-hidden />
				Aktywne zdarzenia
			</div>
			{data.map((inc) => (
				<div key={inc.ruleId} className="text-xs text-foreground/90">
					<span className="font-medium">{inc.ruleName}</span>
					<span className="whitespace-pre-wrap text-muted-foreground"> — {inc.summary}</span>
				</div>
			))}
		</div>
	);
}

/**
 * RustDesk „Import Server Config" string kierujacy klienta na NASZ self-hosted
 * serwer (rust.farmaster.pl). Potrzebny dla laczacych sie z Mac/Linux BEZ agenta
 * Farmaster — `rustdesk://<ID>` niesie tylko ID, nie konfiguracje serwera, wiec
 * swiezy klient celuje w publiczne serwery RustDesk i nie znajdzie ID apteki.
 *
 * Format = `reverse(base64url_nopad(JSON{host,relay,api,key}))`, zweryfikowany z
 * kodem RustDeska (`ServerConfig.decode` w flutter/lib/common.dart: jsonDecode →
 * fallback reverse+base64.normalize+decode; oraz custom_server.rs). Klucz jest
 * PUBLICZNY (sluzy do szyfrowania handshake, rozdawany wszystkim klientom) —
 * bezpiecznie go pokazac. Wartosci = DEFAULT_RUSTDESK_SERVER_* z agenta
 * (device-agent/farmaster-agent-service/src/config.rs). Regeneracja po zmianie
 * serwera/klucza: reverse(base64url_nopad(JSON.stringify({host,relay,api,key}))).
 * Import bez admina: RustDesk → Ustawienia → Sieć → odblokuj → Import Server Config.
 */
const RUSTDESK_SERVER_CONFIG_STRING =
	"0nIsBnLyVGdzFWbyFmZuQ3c1JnI6ISehxWZyJCLiwGcuIXZ0NXYtJXYm5CdzVncv8iOzBHd0hmI6ISawFmIsISP3FEbxd0d4N1QzBDcIp1T05mV4ZDSLJWN4dzZRZmaWFUWUVUciV0NpFVeMJiOikXZrJCLiwGcuIXZ0NXYtJXYm5CdzVnciojI0N3boJye";

/**
 * Reveal a device's RustDesk ID + unattended password for AGENTLESS controllers
 * (e.g. someone on a Mac who dials in by hand). SUPER_ADMIN only — both the UI
 * gate here and the backend endpoint enforce it. Fetched on an explicit click,
 * never auto-loaded.
 */
function ConnectionSecretReveal({
	deviceUuid,
	reachable,
}: {
	deviceUuid: string;
	reachable: boolean;
}) {
	const { hasAnyRole } = useAuth();
	const reveal = useRevealConnectionSecret();
	const rotate = useRotatePasswordMutation();
	const [secret, setSecret] = useState<{
		rustdeskId: string | null;
		unattendedPassword: string | null;
	} | null>(null);

	if (!hasAnyRole(["super_admin"])) return null;

	function onReveal() {
		reveal.mutate(deviceUuid, {
			onSuccess: setSecret,
			onError: () => toast.error("Nie udało się pobrać danych dostępu"),
		});
	}

	function onRotate() {
		if (
			// biome-ignore lint/suspicious/noAlert: Legacy dialog; replace with ConfirmDialog when changing this flow.
			!window.confirm(
				"Ustawić NOWE hasło RustDesk na tym urządzeniu?\n\nStare hasło natychmiast przestanie działać — kto je dostał, straci dostęp.",
			)
		) {
			return;
		}
		rotate.mutate(deviceUuid, {
			onSuccess: (res) => {
				if (!res.ok) {
					toast.error(
						res.error === "device_offline"
							? "Urządzenie offline — nie można zmienić hasła"
							: "Nie udało się zmienić hasła",
					);
					return;
				}
				toast.success("Hasło zmienione — stare jest już nieaktywne");
				// Odśwież ujawnione dane, żeby pokazać aktualne hasło.
				reveal.mutate(deviceUuid, { onSuccess: setSecret });
			},
			onError: () => toast.error("Nie udało się zmienić hasła"),
		});
	}

	function copy(value: string | null, label: string) {
		if (!value) return;
		navigator.clipboard?.writeText(value).then(
			() => toast.success(`Skopiowano ${label}`),
			() => toast.error("Nie udało się skopiować"),
		);
	}

	return (
		<div className="space-y-2 rounded-md bg-muted/40 p-2">
			<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
				<KeyRound className="h-3.5 w-3.5" aria-hidden />
				Dostęp ręczny (bez agenta)
			</div>

			{/* Mac/Linux: jednorazowa konfiguracja serwera RustDesk. `rustdesk://<ID>`
			    niesie tylko ID, nie serwer — bez tego klient celuje w publiczne
			    serwery RustDesk i nie znajdzie apteki. Klucz publiczny, nie sekret. */}
			<div className="space-y-1 border-t border-border/50 pt-1.5">
				<div className="text-xs text-muted-foreground">
					Mac/Linux — raz: RustDesk → Ustawienia → Sieć → odblokuj → „Import Server Config"
				</div>
				<Button
					size="sm"
					variant="outline"
					className="w-full justify-center gap-1.5 max-md:h-9"
					onClick={() => copy(RUSTDESK_SERVER_CONFIG_STRING, "konfigurację serwera")}
				>
					<Copy className="h-3.5 w-3.5" aria-hidden />
					Kopiuj konfigurację serwera
				</Button>
			</div>

			{secret ? (
				<div className="space-y-1.5">
					{(
						[
							["RustDesk ID", secret.rustdeskId],
							["Hasło", secret.unattendedPassword],
						] as const
					).map(([label, value]) => (
						<div key={label} className="flex items-center justify-between gap-2 text-xs">
							<span className="text-muted-foreground">{label}</span>
							<span className="flex items-center gap-1 font-mono text-foreground max-md:min-w-0 max-md:break-all">
								{value ?? "—"}
								{value ? (
									<Button
										size="icon"
										variant="ghost"
										className="h-5 w-5 max-md:size-9"
										aria-label={`Kopiuj ${label}`}
										onClick={() => copy(value, label)}
									>
										<Copy className="h-3 w-3" aria-hidden />
									</Button>
								) : null}
							</span>
						</div>
					))}
					{/* Rotacja: ustawia nowe stałe hasło → odcina czasowy dostęp
					    zewnętrzny. Wymaga online (agent musi przyjąć zmianę). */}
					<Button
						size="sm"
						variant="outline"
						className="w-full justify-center gap-1.5 max-md:h-9"
						onClick={onRotate}
						disabled={!reachable || rotate.isPending}
						title={reachable ? undefined : "Urządzenie offline — nie można zmienić hasła"}
					>
						{rotate.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
						) : (
							<RotateCcw className="h-3.5 w-3.5" aria-hidden />
						)}
						Wymuś zmianę hasła
					</Button>
				</div>
			) : (
				<Button
					size="sm"
					variant="outline"
					className="w-full justify-center gap-1.5 max-md:h-9"
					onClick={onReveal}
					disabled={reveal.isPending}
				>
					{reveal.isPending ? (
						<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
					) : (
						<KeyRound className="h-3.5 w-3.5" aria-hidden />
					)}
					Pokaż ID i hasło
				</Button>
			)}
		</div>
	);
}

function ConnectionPolicyControl({ device }: { device: DeviceAgent }) {
	const mutation = useUpdateConnectionPolicy();
	const effective = connectionPolicyLabel(device.effectiveConnectionPolicy);
	const current = device.connectionPolicy ?? POLICY_AUTO;

	function onChange(value: string) {
		const policy: ConnectionPolicy | null =
			value === POLICY_AUTO ? null : (value as ConnectionPolicy);
		mutation.mutate(
			{ deviceUuid: device.deviceUuid, policy },
			{
				onSuccess: () => toast.success("Zapisano politykę połączenia"),
				onError: () => toast.error("Nie udało się zapisać polityki połączenia"),
			},
		);
	}

	return (
		<div className="space-y-1.5 rounded-md bg-muted/40 p-2">
			<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
				<ShieldCheck className="h-3.5 w-3.5" aria-hidden />
				Polityka połączenia
			</div>
			<Select value={current} onValueChange={onChange} disabled={mutation.isPending}>
				<SelectTrigger
					size="sm"
					className="w-full max-md:data-[size=sm]:h-9"
					aria-label="Polityka połączenia"
				>
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={POLICY_AUTO}>Automatycznie (wg roli)</SelectItem>
					<SelectItem value="unattended">Bez potwierdzenia</SelectItem>
					<SelectItem value="confirm">Wymaga potwierdzenia</SelectItem>
				</SelectContent>
			</Select>
			<p className="text-[11px] text-muted-foreground">
				Aktualnie: {effective.label} — {effective.hint}
			</p>
		</div>
	);
}

function CanaryControl({ device }: { device: DeviceAgent }) {
	const mutation = useUpdateDeviceCanary();

	function toggle() {
		mutation.mutate(
			{ deviceUuid: device.deviceUuid, isCanary: !device.isCanary },
			{
				onSuccess: (d) =>
					toast.success(d.isCanary ? "Oznaczono jako canary" : "Zdjęto status canary"),
				onError: () => toast.error("Nie udało się zmienić statusu canary"),
			},
		);
	}

	return (
		<button
			type="button"
			onClick={toggle}
			disabled={mutation.isPending}
			aria-pressed={device.isCanary}
			title="Kampania w trybie „Canary” obejmuje tylko urządzenia z tą flagą"
			className={cn(
				"inline-flex items-center gap-1 text-[11px] transition-colors disabled:opacity-50 max-md:min-h-9",
				device.isCanary
					? "font-medium text-primary"
					: "text-muted-foreground hover:text-foreground",
			)}
		>
			{mutation.isPending ? (
				<Loader2 className="h-3 w-3 animate-spin" aria-hidden />
			) : (
				<FlaskConical className="h-3 w-3" aria-hidden />
			)}
			{device.isCanary ? "Canary" : "Oznacz jako canary"}
		</button>
	);
}

function AsystentControl({ device }: { device: DeviceAgent }) {
	const mutation = useUpdateAsystentEnabled();
	const excluded = useGroupExcluded(device);
	if (
		!(device.deviceRole === "franchise" || device.deviceRole === "franchise_managed") ||
		device.idfirm == null
	) {
		return null;
	}

	function toggle() {
		mutation.mutate(
			{ deviceUuid: device.deviceUuid, asystentEnabled: !device.asystentEnabled },
			{
				onSuccess: (d) =>
					toast.success(d.asystentEnabled ? "Włączono Asystenta" : "Wyłączono Asystenta"),
				onError: () => toast.error("Nie udało się zmienić ustawienia Asystenta"),
			},
		);
	}

	return (
		<label
			title={excluded ? GROUP_EXCLUDED_TITLE : undefined}
			className="flex cursor-pointer items-center gap-2 text-xs text-foreground max-md:min-h-9"
		>
			<input
				type="checkbox"
				checked={device.asystentEnabled === true}
				onChange={toggle}
				disabled={mutation.isPending || excluded}
			/>
			Asystent Farmaceuty
		</label>
	);
}

/**
 * Opt-in edukacji dymkami (np. o szkodliwych zmianach BLOZ) — zgoda APTEKI
 * (`idfirm`) w organizacji URZĄDZENIA (`tenantId`), NIE urządzenia: zaznaczenie
 * na dowolnym kaflu obejmuje wszystkie stanowiska tej apteki, a stan jest
 * wspólny (jedna lista per organizacja w cache, patrz `useNotificationOptin`).
 * Organizacja pochodzi z urządzenia, nie z przełącznika: panel pokazuje
 * super_adminowi urządzenia wszystkich organizacji naraz. Widoczny tylko na
 * urządzeniach franczyzowych — apteki własne są objęte kanałem powiadomień zawsze.
 */
function EdukacjaControl({ device }: { device: DeviceAgent }) {
	return <PharmacyOptinControl device={device} copy={OPTIN_COPY["notification-optin"]} />;
}

/**
 * Asysta informatyczna — zgoda APTEKI franczyzowej na „Zgłoś problem” ze
 * stanowisk (menu ikony agenta i widget nad zegarem). Ten sam model co opt-in
 * powiadomień; apteki własne mają zgłoszenia zawsze.
 */
function AsystaControl({ device }: { device: DeviceAgent }) {
	return <PharmacyOptinControl device={device} copy={OPTIN_COPY["it-assist-optin"]} />;
}

const OPTIN_COPY = {
	"notification-optin": {
		kind: "notification-optin",
		label: "Powiadomienia edukacyjne (cała apteka)",
		titleOff: "Dymki edukacyjne dla WSZYSTKICH stanowisk tej apteki (zgoda franczyzobiorcy)",
		enabled: "Włączono powiadomienia edukacyjne dla apteki",
		disabled: "Wyłączono powiadomienia edukacyjne dla apteki",
		noDevices: "Włączono, ale apteka nie ma aktywnych stanowisk — dymki nie mają gdzie dotrzeć",
		error: "Nie udało się zmienić ustawienia powiadomień",
	},
	"it-assist-optin": {
		kind: "it-assist-optin",
		label: "Asysta informatyczna (cała apteka)",
		titleOff:
			"„Zgłoś problem” ze stanowisk tej apteki do Helpdesku (franczyza z naszą asystą informatyczną)",
		enabled: "Włączono asystę informatyczną — stanowiska apteki mogą zgłaszać problemy",
		disabled: "Wyłączono asystę informatyczną dla apteki",
		noDevices: "Włączono, ale apteka nie ma aktywnych stanowisk z agentem",
		error: "Nie udało się zmienić asysty informatycznej",
	},
} as const satisfies Record<OptinKind, { kind: OptinKind } & Record<string, string>>;

function PharmacyOptinControl({
	device,
	copy,
}: {
	device: DeviceAgent;
	copy: (typeof OPTIN_COPY)[OptinKind];
}) {
	const tenantId = device.tenantId;
	const { data: optins } = useNotificationOptin(tenantId, copy.kind);
	const mutation = useSetNotificationOptin(copy.kind);
	const excluded = useGroupExcluded(device);
	const idfirm = device.idfirm;
	if (
		!(device.deviceRole === "franchise" || device.deviceRole === "franchise_managed") ||
		idfirm == null ||
		tenantId == null
	) {
		return null;
	}
	const entry = optins?.find((o) => o.idfirm === idfirm);
	const enabled = entry != null;

	function toggle() {
		mutation.mutate(
			{ tenantId: tenantId as number, idfirm: idfirm as number, enabled: !enabled },
			{
				onSuccess: (result) => {
					if (result == null) {
						toast.success(copy.disabled);
					} else if (result.activeDevices === 0) {
						toast.warning(copy.noDevices, { duration: 6000 });
					} else {
						toast.success(copy.enabled);
					}
				},
				onError: () => toast.error(copy.error),
			},
		);
	}

	const title = excluded
		? GROUP_EXCLUDED_TITLE
		: entry
			? `Włączone ${new Date(entry.enabledAt).toLocaleDateString("pl-PL")}${entry.enabledBy ? ` przez ${entry.enabledBy}` : ""} — obejmuje wszystkie stanowiska tej apteki`
			: copy.titleOff;

	return (
		<label
			title={title}
			className="flex cursor-pointer items-center gap-2 text-xs text-foreground max-md:min-h-9"
		>
			<input
				type="checkbox"
				checked={enabled}
				onChange={toggle}
				disabled={mutation.isPending || optins == null || excluded}
			/>
			{copy.label}
		</label>
	);
}

/** Opt-iny franczyzy przegrywają z wykluczeniem — ich przełączniki są wtedy
 *  nieaktywne, żeby nie sugerowały, że Asystent albo dymki zadziałają. */
const GROUP_EXCLUDED_TITLE =
	"Apteka wykluczona z grupy — przywróć ją do grupy, aby to ustawienie działało";

function useGroupExcluded(device: DeviceAgent): boolean {
	const key = pharmacyKey(device);
	const { data: exclusions } = useGroupExclusions(key?.tenantId ?? null);
	return key != null && (exclusions?.some((e) => e.idfirm === key.idfirm) ?? false);
}

/** Urządzenie przypięte do apteki (`idfirm` + organizacja) — tylko takie może
 *  podlegać wykluczeniu apteki z grupy. */
function pharmacyKey(device: DeviceAgent): { tenantId: number; idfirm: number } | null {
	const bound =
		device.deviceRole === "pharmacy_workstation" ||
		device.deviceRole === "franchise" ||
		device.deviceRole === "franchise_managed";
	if (!bound || device.idfirm == null || device.tenantId == null) return null;
	return { tenantId: device.tenantId, idfirm: device.idfirm };
}

/**
 * Wykluczenie APTEKI z grupy (np. sprzedana apteka własna z agentem do czasu
 * przekazania): wszystkie jej stanowiska tracą Asystenta, kanał powiadomień i
 * „Otwórz publiczny KSeF” w tray. Zdalny dostęp, synchronizacja, diagnostyka i
 * aktualizacje działają dalej. Stan per (organizacja urządzenia, idfirm), jak
 * opt-in edukacji — patrz `useGroupExclusions`.
 */
function GroupExclusionControl({ device }: { device: DeviceAgent }) {
	const key = pharmacyKey(device);
	const { data: exclusions } = useGroupExclusions(key?.tenantId ?? null);
	const mutation = useSetGroupExclusion();
	if (key == null) return null;
	const entry = exclusions?.find((e) => e.idfirm === key.idfirm);
	const excluded = entry != null;

	function toggle() {
		if (key == null) return;
		mutation.mutate(
			{ ...key, excluded: !excluded },
			{
				onSuccess: (result) =>
					toast.success(
						result == null ? "Przywrócono aptekę do grupy" : "Wykluczono aptekę z grupy",
					),
				onError: () => toast.error("Nie udało się zmienić wykluczenia apteki"),
			},
		);
	}

	const title = entry
		? `Wykluczona ${new Date(entry.excludedAt).toLocaleDateString("pl-PL")}${entry.excludedBy ? ` przez ${entry.excludedBy}` : ""} — wszystkie stanowiska tej apteki bez Asystenta, powiadomień i KSeF w tray`
		: "Wyklucza CAŁĄ aptekę z Asystenta, powiadomień i KSeF w tray; zdalny dostęp, synchronizacja i aktualizacje działają dalej";

	return (
		<label
			title={title}
			className="flex cursor-pointer items-center gap-2 text-xs text-foreground max-md:min-h-9"
		>
			<input
				type="checkbox"
				checked={excluded}
				onChange={toggle}
				disabled={mutation.isPending || exclusions == null}
			/>
			Wyklucz z grupy aptek (cała apteka)
		</label>
	);
}

/** Plakietka na zwiniętym kafelku: stan wykluczenia widoczny bez rozwijania. */
function GroupExclusionBadge({ tenantId, idfirm }: { tenantId: number; idfirm: number }) {
	const { data: exclusions } = useGroupExclusions(tenantId);
	if (!exclusions?.some((e) => e.idfirm === idfirm)) return null;
	return (
		<span
			className={cn(badgeBase, statusClasses.warning, "gap-1")}
			title="Apteka wykluczona z grupy: bez Asystenta, powiadomień i KSeF w tray"
		>
			<Ban className="h-3 w-3" aria-hidden />
			Poza grupą
		</span>
	);
}

const STATUS_VARIANT: Record<DeviceStatus, StatusVariant> = {
	online: "success",
	offline: "warning",
	error: "error",
	revoked: "error",
	unassigned: "info",
};

const STATUS_LABEL: Record<DeviceStatus, string> = {
	online: "Online",
	offline: "Offline",
	error: "Błąd",
	revoked: "Odwołane",
	unassigned: "Nieprzypisane",
};

const ROLE_LABEL: Record<string, string> = {
	pharmacy_workstation: "Stanowisko apteczne",
	office_laptop: "Laptop biurowy",
	it_admin: "Administrator IT",
	server: "Serwer",
	natubay: "Natubay",
	franchise: "Franczyza",
	franchise_managed: "Franczyza-obsługiwana",
};

function RoleIcon({ role }: { role: DeviceRole }) {
	const cls = "h-4 w-4 text-muted-foreground shrink-0";
	switch (role) {
		case "server":
			return <Server className={cls} aria-hidden />;
		case "office_laptop":
			return <Laptop className={cls} aria-hidden />;
		case "it_admin":
			return <ShieldCheck className={cls} aria-hidden />;
		case "natubay":
			return <Store className={cls} aria-hidden />;
		case "franchise":
		case "franchise_managed":
			return <Building2 className={cls} aria-hidden />;
		default:
			return <Monitor className={cls} aria-hidden />;
	}
}

function StatusBadge({ status }: { status: DeviceStatus }) {
	const variant = STATUS_VARIANT[status] ?? "info";
	return (
		<span className={cn(badgeBase, statusClasses[variant])}>{STATUS_LABEL[status] ?? status}</span>
	);
}

function formatDate(value: string | null): string {
	if (!value) return "—";
	return new Date(value).toLocaleString("pl-PL", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function DetailRow({
	label,
	value,
	mono,
	title,
}: {
	label: string;
	value: string;
	mono?: boolean;
	/** Dymek wartości (np. build info przy wersji agenta). */
	title?: string;
}) {
	return (
		<div className="flex items-baseline justify-between gap-3 text-xs">
			<span className="text-muted-foreground">{label}</span>
			<span
				className={cn(
					"text-right text-foreground max-md:min-w-0 max-md:break-all",
					mono && "font-mono",
				)}
				title={title}
			>
				{value}
			</span>
		</div>
	);
}

/** Reads a string field off the (untyped) snapshot blob, if present. */
function snapshotString(snapshot: unknown, key: string): string | null {
	if (snapshot && typeof snapshot === "object" && key in snapshot) {
		const v = (snapshot as Record<string, unknown>)[key];
		return typeof v === "string" && v.length > 0 ? v : null;
	}
	return null;
}

/**
 * On-demand log bundle (Phase 6). Click enqueues a `request_log_bundle` command,
 * the agent streams a redacted tar.gz back to the backend, the button polls the
 * status and auto-downloads once `ready`. `failed` raises a toast.
 */
function LogBundleButton({ uuid }: { uuid: string }) {
	const request = useRequestLogBundleMutation();
	const [bundleId, setBundleId] = useState<string | null>(null);
	const status = useLogBundleStatusQuery(uuid, bundleId, bundleId !== null);

	const bundleStatus = status.data?.status;
	const collecting =
		bundleId !== null && (bundleStatus === undefined || bundleStatus === "collecting");

	// React to terminal transitions: ready → auto-download; failed → toast.
	useEffect(() => {
		if (!bundleId) return;
		if (bundleStatus === "ready") {
			downloadLogBundle(uuid, bundleId)
				.then(() => toast.success("Pobrano logi urządzenia"))
				.catch(() => toast.error("Nie udało się pobrać logów"))
				.finally(() => setBundleId(null));
		} else if (bundleStatus === "failed") {
			toast.error(
				`Zbieranie logów nie powiodło się: ${status.data?.failureReason ?? "nieznany błąd"}`,
			);
			setBundleId(null);
		}
	}, [bundleId, bundleStatus, status.data?.failureReason, uuid]);

	const busy = request.isPending || collecting;

	function onClick() {
		request.mutate(uuid, {
			onSuccess: (bundle) => setBundleId(bundle.bundleId),
			onError: () => toast.error("Nie udało się zlecić zbierania logów"),
		});
	}

	return (
		<Button
			size="sm"
			variant="outline"
			className="h-7 min-w-40 flex-1 justify-center gap-1.5 max-md:h-9"
			onClick={onClick}
			disabled={busy}
		>
			{busy ? (
				<>
					<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
					Zbieranie logów…
				</>
			) : (
				<>
					<FileText className="h-3.5 w-3.5" aria-hidden />
					Pobierz logi
				</>
			)}
		</Button>
	);
}

/** Kolory kropki stanu (te same tokeny co reszta statusów w panelu). */
const statusDotClass: Record<StatusVariant, string> = {
	success: "bg-status-success-text",
	warning: "bg-status-warning-text",
	error: "bg-status-error-text",
	info: "bg-status-info-text",
};

/**
 * Znacznik „ten host właśnie stawia RustDeska od nowa" — przeżywa odświeżenie
 * strony, więc przycisk reinstalacji nie odblokowuje się po F5 w środku
 * instalacji. Czyszczony, gdy urządzenie zgłosi nowe ID albo po
 * [`PURGE_WAIT_MS`].
 */
const purgeStampKey = (uuid: string) => `zdalnyDostep.rustdeskPurge.${uuid}`;

/** Po tym czasie oddajemy przycisk, nawet gdy ID nie wróciło (nieudana instalacja). */
const PURGE_WAIT_MS = 10 * 60_000;

/**
 * Okno tuż po czystce, w którym ID urządzenia z listy jest niewiarygodne:
 * refetch wystartowany przed czystką może przynieść wartość sprzed niej. Realna
 * reinstalacja trwa ~2 min, więc 30 s nigdy nie zje prawdziwego powrotu ID.
 */
const STALE_ID_GRACE_MS = 30_000;

function readPurgeStamp(uuid: string): number {
	try {
		const raw = localStorage.getItem(purgeStampKey(uuid));
		const ts = raw ? Number(raw) : 0;
		return Number.isFinite(ts) ? ts : 0;
	} catch {
		return 0;
	}
}

function writePurgeStamp(uuid: string): number {
	const now = Date.now();
	try {
		localStorage.setItem(purgeStampKey(uuid), String(now));
	} catch {
		// Prywatny tryb / pełny storage — blokada zadziała wtedy tylko w tej karcie.
	}
	return now;
}

function clearPurgeStamp(uuid: string) {
	try {
		localStorage.removeItem(purgeStampKey(uuid));
	} catch {
		// jw.
	}
}

/** Jeden wiersz w dialogu „Polecenia": ikona + opis po lewej, przycisk akcji po prawej. */
function CommandRow({
	icon: Icon,
	label,
	hint,
	action,
	title,
	pending,
	disabled,
	destructive,
	onRun,
}: {
	icon: LucideIcon;
	label: string;
	/**
	 * Widoczny tekst pod opisem, np. powód blokady. Dymek (`title`) wyłączonego
	 * przycisku nie pojawia się, bo Button ma `disabled:pointer-events-none`.
	 */
	hint?: string;
	action: React.ReactNode;
	title?: string;
	pending?: boolean;
	disabled?: boolean;
	destructive?: boolean;
	onRun: () => void;
}) {
	return (
		<div className="flex items-center justify-between gap-2 text-sm text-foreground">
			<div className="flex min-w-0 items-center gap-2">
				<Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
				<div className="min-w-0">
					<span className="block truncate max-md:whitespace-normal">{label}</span>
					{hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}
				</div>
			</div>
			<Button
				size="sm"
				variant={destructive ? "destructive" : "outline"}
				className="h-8 shrink-0 gap-1 px-3 max-md:h-9"
				disabled={disabled}
				onClick={onRun}
				title={title}
			>
				{pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : null}
				{action}
			</Button>
		</div>
	);
}

/**
 * „Zainstaluj .NET” w „Poleceniach”: ta sama bramka co lista komputerów z
 * banera (agent, grupa „Serwery”, skan, braki, online) i to samo potwierdzenie.
 * Montowane tylko w otwartym dialogu, więc stan floty pobiera się dopiero tu.
 */
function DotnetInstallCommandRow({ uuid, reachable }: { uuid: string; reachable: boolean }) {
	const overview = useDotnetRuntimeQuery(true);
	const install = useDotnetInstallMutation();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const state = overview.data?.devices.find((d) => d.deviceUuid === uuid);
	const blocked = state
		? installBlockReason(state, reachable, overview.data?.installMinAgentVersion ?? "")
		: overview.isError
			? "Nie udało się pobrać stanu .NET"
			: overview.data
				? "Brak stanu .NET dla tego urządzenia"
				: "Sprawdzam stan .NET…";
	const majors = state?.missingMajors ?? [];
	const versions = (overview.data?.installers ?? [])
		.filter((i) => majors.includes(i.major))
		.map((i) => `${i.version} ${i.arch}`);
	return (
		<>
			<CommandRow
				icon={Cpu}
				label={
					majors.length > 0
						? `Zainstaluj .NET ${majors.join(" i ")} x86 (pharmind XF)`
						: ".NET x86 dla pharmind XF"
				}
				hint={blocked ?? undefined}
				action="Zainstaluj"
				title={
					blocked ?? `Cicha instalacja .NET Desktop Runtime ${versions.join(", ")}, bez restartu`
				}
				disabled={blocked !== null || install.isPending}
				pending={install.isPending}
				onRun={() => setConfirmOpen(true)}
			/>
			<ConfirmDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				title="Zainstalować .NET na tym komputerze?"
				description={`.NET Desktop Runtime ${versions.join(", ")}. Instalacja cicha, bez restartu komputera; .NET 9 nie jest usuwany.`}
				variant="warning"
				confirmLabel="Zainstaluj"
				isLoading={install.isPending}
				onConfirm={() =>
					install.mutate([uuid], {
						onSuccess: notifyInstallResult,
						onError: (err) => toast.error(`Nie udało się zlecić instalacji: ${err.message}`),
						onSettled: () => setConfirmOpen(false),
					})
				}
			/>
		</>
	);
}

/**
 * Komunikat błędu aktualizacji agenta. 409 z backendu niesie gotowy polski
 * powód (offline, już aktualne, update w toku, brak/podmiana instalatora na
 * półce, zmieniona wersja) — pokazujemy go wprost; 5xx i błędy sieci dostają
 * ogólny komunikat zamiast technicznego „HTTP 502".
 */
function agentUpdateErrorMessage(err: unknown): string {
	if (err instanceof ApiError && err.status < 500 && err.message.trim()) {
		return err.message;
	}
	return "Nie udało się wysłać aktualizacji agenta";
}

type ShelfState = "loading" | "error" | "missing" | "no-version" | "ok";

function shelfStateOf(installer: {
	// `isPending` (brak danych), nie `isLoading`: wstrzymane zapytanie (przeglądarka
	// offline) ma `isLoading=false` i bez danych wyglądałoby jak „brak instalatora”.
	isPending: boolean;
	isError: boolean;
	data?: { available: boolean; version?: string | null };
}): ShelfState {
	if (installer.isError) return "error";
	if (installer.isPending) return "loading";
	if (installer.isError) return "error";
	if (!installer.data?.available) return "missing";
	return installer.data.version ? "ok" : "no-version";
}

/**
 * Dlaczego „Aktualizuj agenta" jest nieaktywne (`null` = można). Powód jest
 * widoczny pod opisem wiersza, więc operator nie zgaduje, czemu nie da się kliknąć.
 */
function agentUpdateBlockedReason({
	shelfVersion,
	shelfState,
	serviceVersion,
	reachable,
}: {
	shelfVersion: string | null;
	/** Stan półki, gdy `shelfVersion` brak — każdy przypadek ma inną przyczynę. */
	shelfState: ShelfState;
	serviceVersion: string | null;
	reachable: boolean;
}): string | null {
	if (!shelfVersion) {
		switch (shelfState) {
			case "loading":
				return "Sprawdzam wersję instalatora na półce…";
			case "error":
				return "Nie udało się sprawdzić instalatora na półce";
			case "no-version":
				return "Instalator na półce nie ma pliku wersji (.version)";
			default:
				return "Brak instalatora na półce";
		}
	}
	if (!serviceVersion) return "Urządzenie nie zgłosiło wersji agenta";
	if (!isAgentVersionOlder(serviceVersion, shelfVersion)) {
		return `Urządzenie ma już najnowszą wersję ${serviceVersion}`;
	}
	if (!reachable) return "Urządzenie offline — aktualizacja wymaga połączenia";
	return null;
}

/**
 * Naprawy udziału KS w dialogu „Polecenia” — tylko gdy backend wykrył problem
 * (stan z tego samego cache co sekcja „Udział KS”, więc bez nowego zapytania
 * przy rozwiniętym kaflu). Montowane dopiero po otwarciu dialogu.
 */
function KsShareFixRows({
	uuid,
	tenantId,
	idfirm,
	reachable,
}: {
	uuid: string;
	tenantId: number | null;
	idfirm: number | null;
	reachable: boolean;
}) {
	const { data } = useDeviceShareHealthQuery(uuid, tenantId, idfirm, true);
	const retarget = useKsShareFixMutation("apman");
	const grant = useKsShareFixMutation("grant");
	const run = (fix: typeof retarget, ok: string, failure: string) =>
		fix.mutate(uuid, {
			onSuccess: (resp) => (resp.ok ? toast.success(ok) : toast.error(resp.error ?? failure)),
			onError: () => toast.error(failure),
		});
	const stale = data?.current?.role === "station" ? data.staleTarget : null;
	const denied = data?.current?.role === "server" ? data.shareAccessDenied : null;
	return (
		<>
			{stale ? (
				<CommandRow
					icon={FolderSync}
					label="Popraw adres serwera w APMAN.INI"
					hint={`${stale.target} → ${stale.serverIp}${stale.serverHostname ? ` (${stale.serverHostname})` : ""}`}
					action="Popraw"
					title="Przepisuje KATALOG_SIECIOWY i DB_SERVER ze starego adresu serwera KS na bieżący, z kopią zapasową APMAN.INI obok. KS-AOW trzeba potem uruchomić ponownie"
					disabled={!reachable || retarget.isPending}
					pending={retarget.isPending}
					onRun={() => run(retarget, "APMAN.INI poprawiony", "Nie udało się poprawić APMAN.INI")}
				/>
			) : null}
			{denied ? (
				<CommandRow
					icon={FolderSync}
					label={`Udostępnij udział ${denied.share} kontu serwis`}
					hint={`Odmowa dostępu: ${denied.stations.join(", ")}`}
					action="Nadaj"
					title="Dopisuje lokalnemu kontu serwis „Zmiana” na udziale i „Modyfikacja” NTFS na jego katalogu. Istniejących uprawnień nie zmienia"
					disabled={!reachable || grant.isPending}
					pending={grant.isPending}
					onRun={() =>
						run(grant, "Dostęp do udziału nadany", "Nie udało się nadać dostępu do udziału")
					}
				/>
			) : null}
		</>
	);
}

function DiagnosticsPanel({
	uuid,
	snapshot,
	rustdeskId,
	serviceVersion,
	tenantId,
	idfirm,
	defaultCommandsOpen = false,
}: {
	uuid: string;
	snapshot: unknown;
	/** ID RustDeska z wiersza urządzenia — po czystce wraca dopiero po ~2 min. */
	rustdeskId: string | null;
	/** Wersja usługi agenta z wiersza urządzenia — porównywana z instalatorem na półce. */
	serviceVersion: string | null;
	/** Bieżące przypisanie — klucz cache stanu udziału KS (naprawy w „Polecenia”). */
	tenantId: number | null;
	idfirm: number | null;
	defaultCommandsOpen?: boolean;
}) {
	// `enabled` is implicitly true here — the panel only mounts once expanded.
	const { data, isLoading, isError, refetch, isFetching } = useDeviceDiagnosticsQuery(uuid, true);
	const restartSpooler = useRestartSpoolerMutation();
	const killOffice = useKillOfficeMutation();
	const dbOnline = useDbOnlineMutation();
	const unlockServiceAccount = useUnlockServiceAccountMutation();
	const purgeRustdesk = usePurgeRustdeskMutation();
	const agentUpdate = useRequestAgentUpdateMutation();
	// Wersja instalatora na półce (ta sama, którą bierze kampania floty).
	const installer = useInstallerMetadataQuery();
	const { hasAnyRole } = useAuth();
	const isSuperAdmin = hasAnyRole(["super_admin"]);
	// Katalog operacji — tylko super_admin (endpoint require_super_admin); bez
	// `enabled` non-super_admin dostaje 403.
	const operations = useDeviceOperationsQuery(isSuperAdmin);
	const runOperation = useRunDeviceOperationMutation();
	// Dialog trzyma OBIEKT operacji (katalog jest N-elementowy), nie boolean.
	const [pendingOp, setPendingOp] = useState<DeviceOperation | null>(null);
	const [purgeOpen, setPurgeOpen] = useState(false);
	const [agentUpdateOpen, setAgentUpdateOpen] = useState(false);
	// Katalog operacji siedzi za osobnym dialogiem — przycisk „Polecenia" w
	// diagnostyce jest łatwy do omyłkowego kliknięcia, dialog to drugi świadomy krok.
	const [commandsOpen, setCommandsOpen] = useState(defaultCommandsOpen);

	function runOp(op: DeviceOperation) {
		runOperation.mutate(
			{ deviceUuid: uuid, operationId: op.operationId },
			{
				onSuccess: (resp) =>
					toast.success(
						resp.queued
							? `Operacja „${op.label}" zlecona — czeka w kolejce urządzenia`
							: `Operacja „${op.label}" przyjęta`,
					),
				onError: () => toast.error(`Nie udało się zlecić operacji „${op.label}"`),
			},
		);
	}
	// Po czystce backend zeruje ID, a agent stawia instalację od nowa — nowe ID
	// wraca dopiero po ~2 min (zmierzone: Kraków Kombatantów, 113 s). Blokujemy
	// więc przycisk aż do POWROTU ID, a nie na sztywne X sekund. Znacznik siedzi
	// w localStorage, żeby F5 (albo zwinięcie kafla) nie odblokowało przycisku w
	// środku instalacji. Lista i tak polluje wtedy szybciej
	// (`isDeviceProvisioning`).
	const [purgedAt, setPurgedAt] = useState(() => readPurgeStamp(uuid));
	const sincePurge = purgedAt > 0 ? Date.now() - purgedAt : Number.POSITIVE_INFINITY;
	// Przez pierwsze `STALE_ID_GRACE_MS` po czystce NIE ufamy ID z listy: mógł je
	// przynieść refetch wystartowany jeszcze przed czystką, a wtedy blokada
	// znikała natychmiast po kliknięciu (zgłoszone 2026-08-18).
	const awaitingRustdeskId =
		sincePurge < PURGE_WAIT_MS && (sincePurge < STALE_ID_GRACE_MS || !rustdeskId);
	useEffect(() => {
		if (purgedAt <= 0) return;
		const since = Date.now() - purgedAt;
		const finish = () => {
			clearPurgeStamp(uuid);
			setPurgedAt(0);
		};
		if (rustdeskId) {
			// ID wróciło → instalacja gotowa (najwcześniej po oknie nieufności).
			// Diagnostykę pobieramy na nowo, bo w cache siedzi stan sprzed
			// instalacji („brak usługi") i kropka wisiałaby na czerwono do F5.
			const t = setTimeout(
				() => {
					finish();
					void refetch();
				},
				Math.max(0, STALE_ID_GRACE_MS - since),
			);
			return () => clearTimeout(t);
		}
		// Zawór bezpieczeństwa: gdyby instalacja nie doszła do skutku, oddajemy
		// przycisk operatorowi zamiast blokować go w nieskończoność.
		const t = setTimeout(finish, Math.max(0, PURGE_WAIT_MS - since));
		return () => clearTimeout(t);
	}, [purgedAt, rustdeskId, uuid, refetch]);

	const lastRemoteError = snapshotString(snapshot, "lastRemoteAccessError");

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
				Pobieram diagnostykę z urządzenia…
			</div>
		);
	}

	if (isError || !data) {
		return (
			<div className="flex items-center justify-between gap-2 text-xs">
				<span className="text-muted-foreground">Urządzenie nie odpowiedziało.</span>
				<Button size="sm" variant="outline" className="max-md:h-9" onClick={() => refetch()}>
					Ponów
				</Button>
			</div>
		);
	}

	if (data.source === "unavailable" || !data.diagnostics) {
		return (
			<div className="flex items-center justify-between gap-2 text-xs">
				<span className="text-muted-foreground">Brak danych diagnostycznych.</span>
				<Button
					size="sm"
					variant="outline"
					className="max-md:h-9"
					onClick={() => refetch()}
					disabled={isFetching}
				>
					Ponów
				</Button>
			</div>
		);
	}

	const diag = data.diagnostics;
	const rustdeskService = formatRustdeskServiceState(diag.rustdeskServiceState, awaitingRustdeskId);
	const disk = formatDiskAlert(diag);
	const bootHint = formatBootTypeHint(diag.bootType);
	const reachable = data.deviceOnline;

	// Aktualizacja agenta na tym jednym urządzeniu: tylko gdy jest wersja na
	// półce i urządzenie ma STARSZĄ (to samo porównanie co backend, więc
	// aktualne/nowsze mają przycisk wyłączony z widocznym wyjaśnieniem).
	const shelfVersion = installer.data?.available ? (installer.data.version ?? null) : null;
	const agentUpdateBlocked = agentUpdateBlockedReason({
		shelfVersion,
		shelfState: shelfStateOf(installer),
		serviceVersion,
		reachable,
	});

	return (
		<div className="space-y-2">
			{data.source === "cached" ? (
				<div className={cn(badgeBase, statusClasses.warning, "gap-1 max-md:whitespace-normal")}>
					<Clock className="h-3 w-3" aria-hidden />
					Dane z pamięci podręcznej {formatCacheAge(data.collectedAt)}
				</div>
			) : null}

			<div
				className={cn(
					"space-y-1.5 rounded-md border p-2 text-xs",
					disk.low ? statusClasses.error : "border-border",
				)}
			>
				<div className="flex items-center gap-2">
					<HardDrive className="h-3.5 w-3.5 shrink-0" aria-hidden />
					<span className="font-medium">Dysk C:</span>
					<span className={cn("font-mono", disk.low && "font-semibold")}>{disk.label}</span>
					{disk.low ? (
						<span className="ml-auto inline-flex items-center gap-1 font-semibold">
							<AlertTriangle className="h-3.5 w-3.5" aria-hidden />
							Mało miejsca
						</span>
					) : null}
				</div>
				{disk.hasData && disk.usedPct != null ? (
					<div
						className="h-2 overflow-hidden rounded-full bg-muted"
						role="progressbar"
						aria-valuenow={disk.usedPct}
						aria-valuemin={0}
						aria-valuemax={100}
						aria-label={`Zajętość dysku C: ${disk.usedPct}%`}
					>
						<div
							className={cn(
								"h-full rounded-full transition-[width]",
								disk.low ? "bg-destructive" : "bg-primary",
							)}
							style={{ width: `${Math.min(100, Math.max(0, disk.usedPct))}%` }}
						/>
					</div>
				) : null}
			</div>

			<div className="flex items-center gap-2 text-xs text-foreground">
				<Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
				<span className="text-muted-foreground">Czas pracy:</span>
				<span className="font-mono">{formatUptime(diag.uptimeSec)}</span>
			</div>
			{bootHint ? <p className="pl-5 text-[11px] text-muted-foreground">{bootHint}</p> : null}

			<div className="flex items-center gap-2 text-xs text-foreground">
				<Printer className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
				<span className="text-muted-foreground">Bufor wydruku:</span>
				<span>{formatPrinterQueueCount(diag.printerQueueCount)}</span>
			</div>

			{/* Stan usługi RustDeska na hoście — diagnoza „czy zdalny dostęp tam
			    w ogóle żyje". Sama reinstalacja przeniesiona do dialogu „Polecenia". */}
			<div className="flex items-center gap-2 text-xs text-foreground">
				<MonitorX className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
				<span className="text-muted-foreground">Usługa RustDesk:</span>
				{/* Kropka niesie sygnał (zielona/żółta/czerwona), podpis obok —
				    żeby stan dało się odczytać bez rozróżniania kolorów. */}
				<span
					className={cn(
						"inline-block size-2 shrink-0 rounded-full",
						rustdeskService ? statusDotClass[rustdeskService.variant] : "bg-muted-foreground/40",
					)}
					aria-hidden
				/>
				<span
					className={cn("truncate", rustdeskService ? "text-foreground" : "text-muted-foreground")}
				>
					{rustdeskService?.label ?? "brak danych"}
				</span>
			</div>

			{/* Wszystkie polecenia (diagnostyczne + podpisany katalog operacji)
			    siedzą za jednym przyciskiem otwierającym dialog — łatwy do
			    omyłkowego kliknięcia przycisk „w locie" to zły pomysł przy akcjach
			    zmieniających maszynę w aptece. */}
			<Button
				variant="outline"
				size="sm"
				className="w-full justify-center gap-2 max-md:h-9"
				onClick={() => setCommandsOpen(true)}
			>
				<ShieldAlert className="h-3.5 w-3.5 shrink-0 text-status-warning-text" aria-hidden />
				Polecenia
			</Button>

			{diag.localIp ? (
				<div className="flex items-center gap-2 text-xs text-foreground">
					<Server className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
					<span className="text-muted-foreground">Lokalne IP:</span>
					<span className="font-mono">{diag.localIp}</span>
				</div>
			) : null}

			{diag.cpuModel ? (
				<div className="flex items-start gap-2 text-xs text-foreground">
					<Cpu className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
					<span className="text-muted-foreground">Procesor:</span>
					<span className="min-w-0 break-words">
						{diag.cpuModel}
						{diag.cpuLogicalCount != null ? (
							<span className="text-muted-foreground"> · {diag.cpuLogicalCount} rdzeni</span>
						) : null}
					</span>
				</div>
			) : null}

			{diag.ramTotalMb != null && diag.ramAvailableMb != null ? (
				<div className="flex items-center gap-2 text-xs text-foreground">
					<MemoryStick className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
					<span className="text-muted-foreground">RAM:</span>
					<span className="font-mono">
						{Math.round(diag.ramAvailableMb)} / {Math.round(diag.ramTotalMb)} MB wolne
					</span>
				</div>
			) : null}

			{diag.metrics ? (
				<MetricsTripleRow
					label="Obciążenie (ostatnie ~15 min):"
					triple={{
						cpuPct: diag.metrics.cpuPctAvg,
						ramPct: diag.metrics.ramPctAvg,
						diskBusyPct: diag.metrics.diskBusyPctAvg,
					}}
					title="Średnia z ostatniego raportowanego okna agenta (heartbeat)"
				/>
			) : null}

			{lastRemoteError ? (
				<div className={cn(badgeBase, statusClasses.error, "gap-1 whitespace-normal")}>
					<AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
					Ostatni błąd zdalnego dostępu: {lastRemoteError}
				</div>
			) : null}

			<ConfirmDialog
				open={purgeOpen}
				onOpenChange={setPurgeOpen}
				title="Zainstalować RustDeska od nowa?"
				description="Usługa zostanie zatrzymana i wykreślona z systemu, a pliki, konfiguracja i zapisane hasło skasowane. Agent postawi świeżą instalację w ciągu kilku minut."
				variant="destructive"
				confirmLabel="Usuń i zainstaluj od nowa"
				isLoading={purgeRustdesk.isPending}
				onConfirm={() => {
					setPurgeOpen(false);
					purgeRustdesk.mutate(uuid, {
						onSuccess: (resp) => {
							if (resp.ok) {
								toast.success(
									"RustDesk usunięty — agent instaluje go od nowa (nowe ID i hasło za ok. 2 min)",
								);
								setPurgedAt(writePurgeStamp(uuid));
								void refetch();
							} else {
								toast.error(resp.error ?? "Nie udało się wyczyścić instalacji RustDeska");
							}
						},
						onError: () => toast.error("Nie udało się wyczyścić instalacji RustDeska"),
					});
				}}
			>
				<p className="text-xs text-muted-foreground">
					Do czasu ponownej instalacji urządzenie będzie nieosiągalne zdalnie, a jego ID i hasło
					RustDesk zmienią się.
				</p>
			</ConfirmDialog>

			<ConfirmDialog
				open={agentUpdateOpen}
				onOpenChange={setAgentUpdateOpen}
				title="Zaktualizować agenta na tym urządzeniu?"
				description={`Zainstaluje wersję ${shelfVersion ?? "—"} z półki (obecnie ${serviceVersion ?? "—"}) z pominięciem etapu canary kampanii.`}
				variant="warning"
				confirmLabel="Aktualizuj teraz"
				isLoading={agentUpdate.isPending}
				onConfirm={() => {
					if (!shelfVersion) return;
					setAgentUpdateOpen(false);
					agentUpdate.mutate(
						{ deviceUuid: uuid, expectedVersion: shelfVersion },
						{
							onSuccess: (resp) =>
								toast.success(
									`Wysłano aktualizację agenta do ${resp.versionTo} — postęp w Zdalny dostęp → Aktualizacje (kampania #${resp.campaignId})`,
								),
							onError: (err) => toast.error(agentUpdateErrorMessage(err)),
						},
					);
				}}
			>
				<p className="text-xs text-muted-foreground">
					Usługa agenta i helper uruchomią się ponownie — zdalny dostęp zniknie na ok. 1–2 min. Po
					aktualizacji kampania floty pominie to urządzenie.
				</p>
			</ConfirmDialog>

			<Dialog open={commandsOpen} onOpenChange={setCommandsOpen}>
				<DialogContent className="max-w-sm">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<ShieldAlert className="h-4 w-4 shrink-0 text-status-warning-text" aria-hidden />
							Polecenia
						</DialogTitle>
						<DialogDescription>
							Operacje systemowe uruchamiane zdalnie na tym urządzeniu w aptece.
						</DialogDescription>
					</DialogHeader>
					<DialogBody className="space-y-2">
						<CommandRow
							icon={Printer}
							label="Restart + wyczyść bufor wydruku"
							action="Uruchom"
							title="Zatrzymuje bufor, usuwa zawieszone wydruki z kolejki i uruchamia go ponownie"
							disabled={!reachable || restartSpooler.isPending}
							pending={restartSpooler.isPending}
							onRun={() =>
								restartSpooler.mutate(uuid, {
									onSuccess: (resp) => {
										if (resp.ok) {
											toast.success("Bufor wydruku zrestartowany i wyczyszczony");
											void refetch();
										} else {
											toast.error(resp.error ?? "Nie udało się zrestartować bufora wydruku");
										}
									},
									onError: () => toast.error("Nie udało się zrestartować bufora wydruku"),
								})
							}
						/>
						<CommandRow
							icon={Wrench}
							label="Napraw Office (LibreOffice/OpenOffice)"
							action="Uruchom"
							title="Wymusza zamknięcie zawieszonych procesów LibreOffice/OpenOffice (taskkill soffice)"
							disabled={!reachable || killOffice.isPending}
							pending={killOffice.isPending}
							onRun={() =>
								killOffice.mutate(uuid, {
									onSuccess: (resp) => {
										if (resp.ok) {
											const n = resp.killed ?? 0;
											toast.success(
												n > 0 ? `Ubito procesy Office (${n})` : "Brak zawieszonych procesów Office",
											);
										} else {
											toast.error(resp.error ?? "Nie udało się ubić procesów Office");
										}
									},
									onError: () => toast.error("Nie udało się ubić procesów Office"),
								})
							}
						/>
						<CommandRow
							icon={Database}
							label="Baza KS online (WAPTEKA.fdb)"
							action="Uruchom"
							title="Przełącza bazę KS-Apteki z powrotem w tryb online (gfix -online na WAPTEKA.fdb); sprawdza najpierw, czy plik bazy istnieje na tym komputerze"
							disabled={!reachable || dbOnline.isPending}
							pending={dbOnline.isPending}
							onRun={() =>
								dbOnline.mutate(uuid, {
									onSuccess: (resp) => {
										if (resp.ok) {
											toast.success("Baza przełączona w tryb online");
										} else {
											toast.error(resp.error ?? "Nie udało się przełączyć bazy w tryb online");
										}
									},
									onError: () => toast.error("Nie udało się przełączyć bazy w tryb online"),
								})
							}
						/>
						<CommandRow
							icon={KeyRound}
							label="Odblokuj konto serwisowe (serwis)"
							action="Uruchom"
							title="Na serwerze apteki: ustawia znane hasło konta „serwis”, włącza je (net user serwis … + /active:yes), zdejmuje blokadę po błędnych logowaniach i ustawia „hasło nigdy nie wygasa”. Na stanowisku takiego konta nie ma — zamiast tego dopisuje poświadczenie do serwera w sejfie Windows zalogowanego użytkownika (serwer rozpoznawany po słowie „serwer” w nazwie urządzenia)"
							disabled={!reachable || unlockServiceAccount.isPending}
							pending={unlockServiceAccount.isPending}
							onRun={() =>
								unlockServiceAccount.mutate(uuid, {
									onSuccess: (resp) => {
										if (resp.ok) {
											toast.success(
												resp.serverIp
													? `Poświadczenie do serwera ${resp.serverIp} dodane`
													: "Konto serwisowe odblokowane",
											);
										} else {
											toast.error(resp.error ?? "Nie udało się odblokować konta serwisowego");
										}
									},
									onError: () => toast.error("Nie udało się odblokować konta serwisowego"),
								})
							}
						/>
						<KsShareFixRows uuid={uuid} tenantId={tenantId} idfirm={idfirm} reachable={reachable} />
						{/* Aktualizacja agenta z półki na TYM urządzeniu, poza etapem
						    canary kampanii — szybka poprawka dla problematycznego hosta.
						    Endpoint tylko super_admin. */}
						{isSuperAdmin ? (
							<CommandRow
								icon={CircleArrowUp}
								label={shelfVersion ? `Aktualizuj agenta do ${shelfVersion}` : "Aktualizuj agenta"}
								hint={agentUpdateBlocked ?? undefined}
								action="Aktualizuj"
								title={
									agentUpdateBlocked ??
									`Instaluje wersję ${shelfVersion} z półki (obecnie ${serviceVersion}) z pominięciem etapu canary`
								}
								disabled={agentUpdateBlocked !== null || agentUpdate.isPending}
								pending={agentUpdate.isPending}
								onRun={() => setAgentUpdateOpen(true)}
							/>
						) : null}
						{/* Endpoint jak zdalna diagnostyka: super_admin, bez impersonacji. */}
						{isSuperAdmin ? <DotnetInstallCommandRow uuid={uuid} reachable={reachable} /> : null}
						<CommandRow
							icon={MonitorX}
							label="Usługa RustDesk"
							action={awaitingRustdeskId ? "Instalacja w toku…" : "Reinstaluj RustDesk"}
							title={
								awaitingRustdeskId
									? "Agent stawia RustDeska od nowa — przycisk wróci, gdy urządzenie zgłosi nowe ID (zwykle ok. 2 min)"
									: "Zatrzymuje i usuwa usługę RustDeska, kasuje pliki, konfigurację i zapisane hasło; agent stawia świeżą instalację. Pomaga, gdy połączenie zrywa się po kilku sekundach"
							}
							disabled={!reachable || purgeRustdesk.isPending || awaitingRustdeskId}
							pending={purgeRustdesk.isPending || awaitingRustdeskId}
							destructive
							onRun={() => setPurgeOpen(true)}
						/>
						{/* Podpisany katalog operacji — dokładany po stronie serwera,
						    bez update'u agenta. Destrukcyjne przez dodatkowe potwierdzenie. */}
						{operations.data?.map((op) => (
							<CommandRow
								key={op.operationId}
								icon={ShieldAlert}
								label={op.label}
								action="Uruchom"
								disabled={runOperation.isPending}
								pending={runOperation.isPending}
								destructive={op.destructive}
								onRun={() => (op.destructive ? setPendingOp(op) : runOp(op))}
							/>
						))}
					</DialogBody>
				</DialogContent>
			</Dialog>

			<ConfirmDialog
				open={pendingOp !== null}
				onOpenChange={(open) => {
					if (!open) setPendingOp(null);
				}}
				title={pendingOp ? `Uruchomić operację: ${pendingOp.label}?` : ""}
				variant="destructive"
				confirmLabel="Uruchom operację"
				isLoading={runOperation.isPending}
				onConfirm={() => {
					if (!pendingOp) return;
					const op = pendingOp;
					setPendingOp(null);
					runOp(op);
				}}
			>
				<p className="text-xs text-muted-foreground">
					Ta operacja zdejmuje zabezpieczenie systemu Windows (kontrolę aplikacji) na tym urządzeniu
					w aptece. Uruchamiaj tylko świadomie — zmienia konfigurację maszyny, której nie masz
					fizycznie pod ręką.
				</p>
			</ConfirmDialog>
		</div>
	);
}

/** Klasa koloru wartości % wg progów (≥80 krytyczne, ≥60 ostrzeżenie). */
function metricValueClass(pct: number | null | undefined): string {
	const level = metricsLevel(pct);
	if (level === "critical") return "font-semibold text-status-error-text";
	if (level === "warning") return "font-semibold text-status-warning-text";
	return "text-foreground";
}

function MetricsTripleRow({
	label,
	triple,
	title,
}: {
	label: string;
	triple: MetricsTriple;
	title: string;
}) {
	return (
		<div
			className="flex items-center gap-2 text-xs max-md:flex-wrap max-md:gap-y-0.5"
			title={title}
		>
			<span className="text-muted-foreground">{label}</span>
			<span className="font-mono">
				<span className="text-muted-foreground">CPU </span>
				<span className={metricValueClass(triple.cpuPct)}>{formatMetricPct(triple.cpuPct)}</span>
				<span className="text-muted-foreground"> · RAM </span>
				<span className={metricValueClass(triple.ramPct)}>{formatMetricPct(triple.ramPct)}</span>
				<span className="text-muted-foreground"> · Dysk </span>
				<span className={metricValueClass(triple.diskBusyPct)}>
					{formatMetricPct(triple.diskBusyPct)}
				</span>
			</span>
		</div>
	);
}

/**
 * "Typowe wykorzystanie" maszyny z 30 dni pracy (mediana 15-min okien) +
 * szczyty p95 + mini-wykres dzienny CPU. Liczone TYLKO z okien, w których
 * komputer działał — postoje nie zaniżają wyniku. Dane z heartbeatów agenta
 * ≥ 0.1.46; starszy agent / świeża instalacja → stan "zbieranie danych".
 */
function MetricsSummarySection({ uuid }: { uuid: string }) {
	// Panel montuje się dopiero po rozwinięciu kafla, więc `enabled` = true.
	const { data, isLoading, isError } = useDeviceMetricsSummaryQuery(uuid, true);

	// Błąd (np. stary backend bez endpointu) → po prostu nie pokazuj sekcji.
	if (isError) return null;

	if (isLoading) {
		return (
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<Activity className="h-3.5 w-3.5" aria-hidden />
				Wczytywanie obciążenia…
			</div>
		);
	}

	if (!data || data.windowCount === 0 || !hasAnyMetric(data.typical)) {
		return (
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<Activity className="h-3.5 w-3.5 shrink-0" aria-hidden />
				Zbieranie danych o obciążeniu — wyniki po ~24 godz. pracy (wymaga agenta ≥ 0.1.46).
			</div>
		);
	}

	return (
		<div className="space-y-1.5 rounded-md border border-border p-2">
			<div className="flex items-center gap-1.5 text-xs font-medium">
				<Activity className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
				Wykorzystanie zasobów ({data.days} dni pracy)
			</div>
			<MetricsTripleRow
				label="Typowo:"
				triple={data.typical}
				title="Mediana ze średnich 15-minutowych okien — tylko z czasu, gdy komputer był włączony"
			/>
			<MetricsTripleRow
				label="Szczyty:"
				triple={data.peak}
				title="95. percentyl maksimów 15-minutowych okien — tak wygląda maszyna w najgorszych momentach"
			/>
			{data.daily.length > 1 ? (
				<div
					className="flex h-16 items-end gap-px"
					role="img"
					aria-label="Dzienny przebieg obciążenia CPU"
					title="Średnie dzienne obciążenie CPU (skala względna — najwyższy dzień = pełna wysokość)"
				>
					{/* Skala względna do maksimum serii: dzienne CPU to zwykle
					    5-15%, więc absolutne % dawało słupki 2-3px (nieczytelne).
					    ponytail: max-normalize, wystarczy do porównania dni. */}
					{(() => {
						const peak = Math.max(...data.daily.map((d) => d.cpuPctAvg ?? 0), 1);
						return data.daily.map((d) => {
							const pct = d.cpuPctAvg;
							const h = pct == null ? 0 : Math.max(6, Math.min(100, (pct / peak) * 100));
							return (
								<div
									key={d.date}
									className="min-w-0 flex-1 rounded-sm bg-primary"
									// Opacity inline, nie przez sufiks klasy (`bg-primary/60`) —
									// Tailwind v4 JIT potrafi zgubić wariant opacity (TRAP).
									style={{ height: `${h}%`, opacity: pct == null ? 0.15 : 0.6 }}
									title={`${d.date}: CPU ${formatMetricPct(pct)} · RAM ${formatMetricPct(d.ramPctAvg)} · Dysk ${formatMetricPct(d.diskBusyPctAvg)}`}
								/>
							);
						});
					})()}
				</div>
			) : null}
		</div>
	);
}

/**
 * Notatki „na urządzeniu": mała ikonka przy „Połącz" otwiera popover z polem
 * wiadomości + historią. Wiadomość helper pokazuje na hoście w oknie
 * zawsze-na-wierzchu. Per-device, tylko gdy online (offline → 409). Historia
 * pokazuje status odczytu (kto + kiedy zamknął okno). Zapytanie o historię
 * leniwe — odpala się dopiero po otwarciu popovera (nie dla każdego kafelka).
 */
function DeviceNotesButton({ device }: { device: DeviceAgent }) {
	const uuid = device.deviceUuid;
	const reachable = isDeviceReachable(device);
	const [open, setOpen] = useState(false);
	const [body, setBody] = useState("");
	const notes = useDeviceNotesQuery(uuid, open);
	const leave = useLeaveNoteMutation();

	const trimmed = body.trim();
	const canSend = reachable && trimmed.length > 0 && !leave.isPending;

	function submit() {
		if (!canSend) return;
		// Emotikony :) ;) :D … → emoji przy wysyłce, żeby backend zapisał już
		// prawdziwe znaki i helper wyrenderował je natywnie (bez zmian w agencie).
		leave.mutate(
			{ deviceUuid: uuid, body: emojifyEmoticons(trimmed) },
			{
				onSuccess: () => {
					toast.success("Notatka wyświetlona na urządzeniu");
					setBody("");
				},
				onError: (err) => {
					const msg = err?.message ?? "";
					toast.error(
						/409|offline/i.test(msg)
							? "Urządzenie offline — notatkę można wysłać tylko gdy jest online"
							: "Nie udało się wysłać notatki",
					);
				},
			},
		);
	}

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					size="icon"
					variant="ghost"
					className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground max-md:size-9"
					aria-label="Zostaw notatkę dla użytkownika"
					title="Zostaw notatkę dla użytkownika"
				>
					<StickyNote className="h-4 w-4" aria-hidden />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80 space-y-2">
				<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
					<StickyNote className="h-3.5 w-3.5" aria-hidden />
					Notatka dla użytkownika
				</div>

				<textarea
					value={body}
					onChange={(e) => setBody(e.target.value)}
					rows={3}
					maxLength={2000}
					disabled={!reachable}
					placeholder={
						reachable
							? "Wiadomość pokaże się w oknie na ekranie użytkownika…"
							: "Urządzenie offline — notatkę można wysłać tylko gdy online"
					}
					className="w-full resize-y rounded-md border border-input bg-background px-2 py-1.5 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
				/>
				<div className="flex justify-end">
					<Button
						size="sm"
						variant="outline"
						className="h-7 gap-1.5 px-2 text-[11px] max-md:h-9"
						disabled={!canSend}
						onClick={submit}
					>
						{leave.isPending ? (
							<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
						) : (
							<Send className="h-3.5 w-3.5" aria-hidden />
						)}
						Wyślij notatkę
					</Button>
				</div>

				{notes.data && notes.data.length > 0 ? (
					<div className="max-h-64 space-y-1.5 overflow-y-auto border-t border-border/60 pt-2">
						<div className="text-[11px] font-medium text-muted-foreground">Historia notatek</div>
						{notes.data.map((n) => (
							<div key={n.noteId} className="rounded-md bg-muted/40 px-2 py-1.5 text-xs">
								<div className="flex items-center justify-between gap-2">
									<span className="font-medium">{n.authorName}</span>
									<span className="text-[10px] text-muted-foreground">
										{formatDate(n.createdAt)}
									</span>
								</div>
								<div className="mt-0.5 whitespace-pre-wrap break-words text-foreground/90">
									{n.body}
								</div>
								<div className="mt-1">
									{n.readAt ? (
										<span className={cn(badgeBase, statusClasses.success, "gap-1")}>
											Odczytano {formatDate(n.readAt)}
										</span>
									) : (
										<span className={cn(badgeBase, "border-border text-muted-foreground")}>
											Wysłano — nieodczytane
										</span>
									)}
								</div>
							</div>
						))}
					</div>
				) : null}
			</PopoverContent>
		</Popover>
	);
}

interface DeviceTileProps {
	device: DeviceAgent;
	/** Numer stanowiska w aptece („Identyfikuj”). */
	stationNumber?: number;
	/** Od razu rozwinięty (panel stanowisk helpdesku: diagnostyka po jednym kliknięciu). */
	defaultOpen?: boolean;
	/** Od razu z otwartym dialogiem „Polecenia” (przycisk w wierszu helpdesku). */
	defaultCommandsOpen?: boolean;
}

export function DeviceTile({
	device,
	stationNumber,
	defaultOpen = false,
	defaultCommandsOpen,
}: DeviceTileProps) {
	const { hasAnyRole } = useAuth();
	const [open, setOpen] = useState(defaultOpen);
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	// Trwale usuniecie (z opcja zdalnej deinstalacji) to operacja flotowa — tylko
	// super_admin. Backend egzekwuje to samo; tu chowamy przycisk i dialog.
	const canDelete = hasAnyRole(["super_admin"]);
	// Rozwiniecie kafelka (diagnostyka, sekrety, edycja przypisania) tylko dla
	// super_admina — pozostale role nie maja wgladu w te szczegoly.
	const canExpand = hasAnyRole(["super_admin"]);
	const roleLabel = ROLE_LABEL[device.deviceRole] ?? device.deviceRole;
	// Reachability = live hub presence, not the lagging `status` column.
	const reachable = isDeviceReachable(device);
	const displayStatus = effectiveDeviceStatus(device);
	const isOffline = !reachable;
	const pharmacy = pharmacyKey(device);

	return (
		<Collapsible
			open={open}
			onOpenChange={setOpen}
			className={cn(
				"w-[calc(21rem+100px)] max-w-full shrink-0 rounded-lg border shadow-xs max-md:w-full",
				isOffline
					? "border-border/60 bg-muted/75 text-muted-foreground"
					: "border-border bg-card text-card-foreground",
			)}
			data-device-status={displayStatus}
		>
			<div className="flex flex-col gap-2 p-3">
				<div className="grid grid-cols-[1fr_auto] items-start gap-x-2 max-md:grid-cols-1 max-md:gap-y-2">
					<div className="flex min-w-0 items-start gap-3">
						<RoleIcon role={device.deviceRole} />
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-2">
								<StationNumber number={stationNumber} name={deviceLabel(device)} />
								<span
									className={cn(
										"truncate font-medium",
										isOffline ? "text-foreground/75" : "text-foreground",
									)}
								>
									{deviceLabel(device)}
								</span>
								<StatusBadge status={displayStatus} />
								{pharmacy ? <GroupExclusionBadge {...pharmacy} /> : null}
								{device.hasActiveEvent ? (
									<span
										className={cn(badgeBase, statusClasses.warning, "gap-1")}
										title="Aktywne zdarzenie (uptime/dysk) — rozwiń, by zobaczyć szczegóły"
									>
										<AlertTriangle className="h-3 w-3" aria-hidden />
									</span>
								) : null}
								{device.effectiveConnectionPolicy === "confirm" ? (
									<span
										className={cn(badgeBase, statusClasses.info, "gap-1")}
										title="Połączenie wymaga akceptacji użytkownika przy urządzeniu"
									>
										<ShieldAlert className="h-3 w-3" aria-hidden />
										Wymaga potwierdzenia
									</span>
								) : null}
							</div>
							<div className="mt-0.5 truncate text-xs text-muted-foreground">
								{roleLabel}
								{device.hostname ? ` · ${device.hostname}` : ""}
							</div>
							{!reachable ? (
								<div className="mt-0.5 text-xs text-muted-foreground">
									Ostatnio widziane: {formatDate(device.lastSeenAt)}
								</div>
							) : null}
						</div>
					</div>

					<div className="flex shrink-0 items-center gap-1 max-md:w-full">
						<ConnectButton device={device} className="max-md:flex-1" />
						{canExpand ? (
							<Button
								size="icon"
								variant="ghost"
								className="max-md:size-11"
								aria-label={open ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
								aria-expanded={open}
								onClick={() => setOpen((v) => !v)}
							>
								<ChevronDown
									className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
									aria-hidden
								/>
							</Button>
						) : null}
					</div>
				</div>

				{/* Dół kafelka: notatka operatora po lewej, notatka „na urządzeniu"
				    w prawym dolnym rogu — z dala od „Połącz", żeby nie kliknąć przypadkiem. */}
				<div className="flex items-end gap-1">
					<div className="min-w-0 flex-1">
						<DeviceOperatorNote
							deviceUuid={device.deviceUuid}
							operatorNote={device.operatorNote}
							layout="stacked"
						/>
					</div>
					<DeviceNotesButton device={device} />
				</div>
			</div>

			<EditDeviceDialog device={device} open={editOpen} onOpenChange={setEditOpen} />
			{canDelete ? (
				<DeleteDeviceDialog device={device} open={deleteOpen} onOpenChange={setDeleteOpen} />
			) : null}

			<CollapsibleContent>
				<div
					className={cn(
						"space-y-3 border-t px-3 py-3",
						isOffline ? "border-border/60" : "border-border",
					)}
				>
					<div className="space-y-1.5">
						<DetailRow label="RustDesk ID" value={device.rustdeskId ?? "—"} mono />
						<DetailRow label="UUID" value={device.deviceUuid} mono />
						<DetailRow
							label="Wersja agenta"
							value={device.serviceVersion ?? "—"}
							mono
							title={
								device.buildInfo
									? `Build: ${versionWithBuild(device.serviceVersion, device.buildInfo)}`
									: undefined
							}
						/>
					</div>

					{/* Edycja przypisania — celowo dopiero w rozwinietych szczegolach,
					    nie na belce, zeby nie kusic przypadkowa zmiana roli/apteki. */}
					<Button
						variant="outline"
						size="sm"
						className="w-full justify-center gap-1.5 max-md:h-9"
						onClick={() => setEditOpen(true)}
					>
						<Pencil className="h-3.5 w-3.5" aria-hidden />
						Edytuj przypisanie
					</Button>

					{open && device.hasActiveEvent ? <ActiveEventsSection uuid={device.deviceUuid} /> : null}

					<ConnectionPolicyControl device={device} />

					<ConnectionSecretReveal deviceUuid={device.deviceUuid} reachable={reachable} />

					<div className="space-y-2 rounded-md bg-muted/40 p-2">
						<div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
							<Wrench className="h-3.5 w-3.5" aria-hidden />
							Diagnostyka
						</div>
						{open ? (
							<DiagnosticsPanel
								uuid={device.deviceUuid}
								snapshot={device.snapshot}
								rustdeskId={device.rustdeskId}
								serviceVersion={device.serviceVersion}
								tenantId={device.tenantId}
								idfirm={device.idfirm}
								defaultCommandsOpen={defaultCommandsOpen}
							/>
						) : null}
						{/* Poza DiagnosticsPanel: metryki czytają DB, więc mają się
						    pokazywać także gdy żywa diagnostyka jest niedostępna. */}
						{open ? <MetricsSummarySection uuid={device.deviceUuid} /> : null}
						{/* Też poza DiagnosticsPanel (historia z DB); montowana dopiero po
						    rozwinięciu, więc seria nie jest pobierana dla całej listy. */}
						{open ? (
							<Suspense
								fallback={
									<div className="text-xs text-muted-foreground">
										Wczytywanie sekcji jakości internetu…
									</div>
								}
							>
								<NetworkQualitySection device={device} />
							</Suspense>
						) : null}
						{/* Udział KS (apteki własne): stan z DB, ukryty bez raportu agenta.
						    Przypisanie w kluczu cache — po przeniesieniu bez stanu starej apteki. */}
						{open ? (
							<ShareHealthSection
								uuid={device.deviceUuid}
								tenantId={device.tenantId}
								idfirm={device.idfirm}
							/>
						) : null}
						{/* Canary w wierszu logów — nie zajmuje osobnego wiersza pod sekcjami. */}
						<div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
							{open ? <LogBundleButton uuid={device.deviceUuid} /> : null}
							<CanaryControl device={device} />
						</div>
						<AsystentControl device={device} />
						<EdukacjaControl device={device} />
						<AsystaControl device={device} />
						<GroupExclusionControl device={device} />
						{/* Stanowisko sync apteki: tylko z dostępem do panelu synchronizacji
						    i dla urządzeń przypisanych do apteki (sekcja sama to sprawdza). */}
						<DeviceSyncSection
							deviceUuid={device.deviceUuid}
							deviceName={deviceLabel(device)}
							idfirm={device.idfirm}
							tenantId={device.tenantId}
							reachable={reachable}
						/>
					</div>

					{/* Trwałe usunięcie — destrukcyjne i flotowe, więc tylko super_admin,
					    na samym dole i za dialogiem, żeby nie pomylić z odwołaniem. */}
					{canDelete ? (
						<Button
							variant="ghost"
							size="sm"
							className="w-full justify-center gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive max-md:h-9"
							onClick={() => setDeleteOpen(true)}
						>
							<Trash2 className="h-3.5 w-3.5" aria-hidden />
							Usuń urządzenie
						</Button>
					) : null}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}
